import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

/* Shared "inside the platform" screenshot gallery — used by the Flagstar and
 * Giggle proposals. Screens come from a live client environment (redacted).
 * Pass `lightbox` to make screens click-to-expand. */

const SCREENS = [
  {
    src: "/screens/fs-overview.png",
    t: "Vendor management — leadership view",
    b: "Tier distribution and portfolio posture across every third party — the scorecard each dedicated vendor environment feeds into.",
  },
  {
    src: "/screens/fs-compliance-status.png",
    t: "Compliance checklist matrix",
    b: "Every vendor held to the same checklist — SOC 2, OFAC, insurance, contracts — one standard, enforced by the prompt library.",
  },
  {
    src: "/screens/fs-thirdparties.png",
    t: "All third parties, one screen",
    b: "Screening status, risk scores, and audits per vendor — with continuous OFAC / PEP / negative-news monitoring behind every row.",
  },
  {
    src: "/screens/complycube.png",
    t: "Powered by our ComplyCube integration",
    b: "Identity and business verification runs through our ComplyCube integration — document + biometric checks at onboarding, then continuous watchlist re-screening.",
    badge: "ComplyCube integration",
  },
];

export function PlatformScreens({
  id = "inside",
  eyebrowClass = "fs-eyebrow",
  headingClass = "fs-h",
  cardClass = "fs-card",
  accent = "#00586B",
  accentSpan = null,
  wrapClass = "max-w-6xl mx-auto px-6 md:px-10",
  lightbox = false,
  eyebrow = "Inside the platform · real screens",
  headingText = "This is what your team logs into.",
  headingAccentText = "Not a mock-up.",
  intro = null,
  footnote = "Screens from a live client environment — client identity redacted. Sample data shown.",
}) {
  const [open, setOpen] = useState(null); // index or null
  const close = useCallback(() => setOpen(null), []);
  const step = useCallback((d) => setOpen((i) => (i === null ? null : (i + d + SCREENS.length) % SCREENS.length)), []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, close, step]);

  return (
    <section id={id} className="fs-anchor anchor-target py-14 md:py-20" data-testid="platform-screens">
      <div className={wrapClass}>
        <div className={eyebrowClass}>{eyebrow}</div>
        <h2 className={`${headingClass} text-4xl md:text-5xl mt-3 max-w-3xl`}>
          {headingText}{" "}
          {accentSpan ? <span className={accentSpan}>{headingAccentText}</span> : <span style={{ color: accent }}>{headingAccentText}</span>}
        </h2>
        {intro && (
          <p className="mt-4 text-lg max-w-3xl leading-relaxed" style={{ opacity: 0.72 }} data-testid="platform-screens-intro">
            {intro}
          </p>
        )}
        <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-5">
          {SCREENS.map(({ src, t, b, badge }, i) => (
            <figure key={t} className={`${cardClass} overflow-hidden`} data-testid={`platform-screen-${i}`}>
              <div
                className={`border-b relative group ${lightbox ? "cursor-zoom-in" : ""}`}
                style={{ borderColor: "rgba(26,26,26,0.08)", background: "#fff" }}
                onClick={lightbox ? () => setOpen(i) : undefined}
                data-testid={`platform-screen-img-${i}`}
              >
                <img src={src} alt={t} loading="lazy" className="w-full h-auto block" />
                {lightbox && (
                  <span className="absolute top-2.5 right-2.5 h-8 w-8 rounded-md grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "rgba(26,26,26,0.65)", color: "#fff" }}>
                    <Maximize2 className="h-4 w-4" />
                  </span>
                )}
              </div>
              <figcaption className="p-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`${headingClass} text-base`}>{t}</div>
                  {badge && (
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] px-2 py-1 rounded-full"
                          style={{ background: `${accent}12`, color: accent }}>{badge}</span>
                  )}
                </div>
                <p className="text-[13px] leading-relaxed mt-1.5" style={{ opacity: 0.7 }}>{b}</p>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-4 text-[12px]" style={{ opacity: 0.55 }}>
          {footnote}
          {lightbox && " Click any screen to view it full-size."}
        </p>
      </div>

      {lightbox && open !== null && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-4 md:p-8"
          style={{ background: "rgba(10,14,20,0.92)", backdropFilter: "blur(6px)" }}
          onClick={close}
          data-testid="screen-lightbox"
        >
          <button type="button" onClick={close} aria-label="Close"
                  data-testid="lightbox-close"
                  className="absolute top-4 right-4 h-11 w-11 rounded-full grid place-items-center text-white transition-colors hover:bg-white/15"
                  style={{ background: "rgba(255,255,255,0.08)" }}>
            <X className="h-5 w-5" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous"
                  data-testid="lightbox-prev"
                  className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full grid place-items-center text-white transition-colors hover:bg-white/15"
                  style={{ background: "rgba(255,255,255,0.08)" }}>
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next"
                  data-testid="lightbox-next"
                  className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full grid place-items-center text-white transition-colors hover:bg-white/15"
                  style={{ background: "rgba(255,255,255,0.08)" }}>
            <ChevronRight className="h-6 w-6" />
          </button>
          <img
            src={SCREENS[open].src}
            alt={SCREENS[open].t}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[82vh] max-w-full w-auto rounded-lg shadow-2xl"
            data-testid="lightbox-image"
          />
          <div className="mt-4 text-center max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-white font-bold text-[15px]">{SCREENS[open].t}</div>
            <p className="text-white/65 text-[13px] mt-1 leading-relaxed">{SCREENS[open].b}</p>
            <div className="text-white/40 text-[11px] mt-2">{open + 1} / {SCREENS.length} · esc to close</div>
          </div>
        </div>
      )}
    </section>
  );
}
